import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FolderIcon,
  FolderPlusIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { useToast } from '@/hooks/use-toast';
import {
  useAddContactsToLists,
  useAddListMembers,
  useCreateList,
  useGetLists,
  useStartAddAllListMembersJob,
  useSyncContactListMembership,
} from '@/modules/lists/hooks/use-lists';
import type { ListMembershipJobApi } from '@/modules/lists/types/lists-api.types';
import type { ContactSelectAllSnapshot } from '@/modules/lists/types/lists.types';

type AddToListModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactName: string;
  contactId?: string;
  /** When provided (e.g. bulk select), adds these contacts to the chosen lists. */
  contactIds?: string[];
  currentLists?: string[];
  onAdd: (lists: string[]) => void;
  /**
   * "Select all matching": instead of ids, the table's filter at the moment of confirming plus the
   * rows the user unticked. The add then runs as a background job on the server, handed to
   * `onJobStarted` to follow; `onAdd` is not called, since nothing has been added yet.
   */
  selectAll?: {
    snapshot: ContactSelectAllSnapshot;
    excludeContactIds: string[];
  };
  onJobStarted?: (job: ListMembershipJobApi, listNames: string[]) => void;
};

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `add-all-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const listNameKey = (name: string) => name.trim().toLowerCase();

export const AddToListModal = ({
  open,
  onOpenChange,
  contactName,
  contactId,
  contactIds,
  currentLists = [],
  onAdd,
  selectAll,
  onJobStarted,
}: AddToListModalProps) => {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [listIdsByName, setListIdsByName] = useState<Record<string, string>>({});
  const [extraMemberListNames, setExtraMemberListNames] = useState<string[]>([]);

  const { data, isLoading, isFetching, isError, error } = useGetLists(
    { pageNo: 1, pageSize: 100 },
    { enabled: open }
  );

  const { mutateAsync: syncMembership, isPending: isSyncing } =
    useSyncContactListMembership();
  const { mutateAsync: addContacts, isPending: isAdding } = useAddContactsToLists();
  const { mutateAsync: createList, isPending: isCreating } = useCreateList();
  const { mutateAsync: addMembers, isPending: isAddingCreatedMembers } = useAddListMembers();
  const { mutateAsync: startAddAllJob, isPending: isStartingJob } = useStartAddAllListMembersJob();
  // One key per opening of the dialog, so a double-clicked "Add" queues one job, not two.
  const idempotencyKeyRef = useRef<string>(newIdempotencyKey());

  const lists = useMemo(
    () => (data?.items ?? []).filter((list) => Boolean(list.name.trim())),
    [data?.items]
  );

  const existingListNames = useMemo(
    () => new Set(lists.map((list) => listNameKey(list.name))),
    [lists]
  );

  const resolvedContactIds = useMemo(() => {
    if (contactIds && contactIds.length > 0) return contactIds.filter(Boolean);
    if (contactId) return [contactId];
    return [];
  }, [contactId, contactIds]);

  const isBulk = Boolean(selectAll) || resolvedContactIds.length > 1;

  const memberListNames = useMemo(() => {
    const names = isBulk ? [] : currentLists.filter(Boolean);
    return Array.from(new Set([...names, ...extraMemberListNames]));
  }, [currentLists, extraMemberListNames, isBulk]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const list of lists) {
      next[listNameKey(list.name)] = list.id;
    }
    setListIdsByName((prev) => ({ ...next, ...prev }));
  }, [lists]);

  useEffect(() => {
    if (!open) {
      setExtraMemberListNames([]);
      return;
    }
    idempotencyKeyRef.current = newIdempotencyKey();
    setSearch('');
    setIsCreatingList(false);
    setNewListName('');
    setExtraMemberListNames([]);
    setSelectedLists(isBulk ? [] : currentLists.filter(Boolean));
    // Reset only when the dialog opens so in-progress checkbox edits are not overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentLists/isBulk are captured at open
  }, [open]);

  const filteredLists = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return lists;
    return lists.filter((list) => list.name.toLowerCase().includes(query));
  }, [lists, search]);

  const newlySelectedCount = selectedLists.filter(
    (list) => !memberListNames.includes(list)
  ).length;
  const hasSelectionChanged =
    memberListNames.length === 0
      ? selectedLists.length > 0
      : selectedLists.length !== memberListNames.length ||
        selectedLists.some((list) => !memberListNames.includes(list));

  const toggleList = (list: string) => {
    setSelectedLists((prev) =>
      prev.includes(list) ? prev.filter((item) => item !== list) : [...prev, list]
    );
  };

  const resolveListIds = (names: string[]) =>
    names
      .map((name) => listIdsByName[listNameKey(name)])
      .filter((id): id is string => Boolean(id));

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (!name) {
      toast({
        variant: 'destructive',
        title: 'List name required',
        description: 'Enter a name for the new list.',
      });
      return;
    }

    const nameKey = listNameKey(name);
    const alreadyExists = existingListNames.has(nameKey);

    try {
      let listId = listIdsByName[nameKey];
      if (!listId) {
        const existing = lists.find((list) => listNameKey(list.name) === nameKey);
        if (existing) {
          listId = existing.id;
        } else {
          const created = await createList({ name, description: '' });
          listId = created.itemId;
        }
      }

      // Under "select all" the new list is only selected here; the job started by "Add" fills it.
      if (!selectAll && resolvedContactIds.length > 0) {
        await addMembers({ listId, contactIds: resolvedContactIds });
        setExtraMemberListNames((prev) =>
          prev.includes(name) ? prev : [...prev, name]
        );
      }

      setListIdsByName((prev) => ({ ...prev, [nameKey]: listId }));

      const listAlreadySelected = selectedLists.includes(name);
      const nextSelectedLists = listAlreadySelected ? selectedLists : [...selectedLists, name];
      setSelectedLists(nextSelectedLists);
      setNewListName('');
      setIsCreatingList(false);
      if (!selectAll) {
        onAdd(nextSelectedLists);
      }

      toast({
        variant: 'success',
        title: alreadyExists ? 'List updated' : 'List created',
        description:
          !selectAll && resolvedContactIds.length > 0
            ? alreadyExists
              ? `${contactName} ${isBulk ? 'were' : 'was'} added to "${name}".`
              : `"${name}" was created and ${contactName} was added to it.`
            : alreadyExists
              ? `"${name}" was updated.`
              : `"${name}" was created.`,
      });
    } catch (createError) {
      toast({
        variant: 'destructive',
        title: 'Failed to create list',
        description:
          createError instanceof Error
            ? createError.message
            : 'Could not create the list.',
      });
    }
  };

  const handleAdd = async () => {
    try {
      const selectedListIds = resolveListIds(selectedLists);

      if (selectAll) {
        if (selectedListIds.length === 0) {
          toast({
            variant: 'destructive',
            title: 'Select a list',
            description: 'Choose at least one list to add contacts to.',
          });
          return;
        }
        const job = await startAddAllJob({
          listIds: selectedListIds,
          selection: selectAll.snapshot,
          excludeContactIds: selectAll.excludeContactIds,
          idempotencyKey: idempotencyKeyRef.current,
        });
        onJobStarted?.(job, selectedLists);
        onOpenChange(false);
        return;
      }

      if (contactId) {
        await syncMembership({
          contactId,
          selectedListIds,
          currentListIds: resolveListIds(memberListNames),
        });
      } else if (resolvedContactIds.length > 0) {
        if (selectedLists.length === 0) {
          toast({
            variant: 'destructive',
            title: 'Select a list',
            description: 'Choose at least one list to add contacts to.',
          });
          return;
        }
        if (selectedListIds.length === 0) {
          toast({
            variant: 'destructive',
            title: 'Select a list',
            description: 'Choose at least one list to add contacts to.',
          });
          return;
        }
        await addContacts({
          listIds: selectedListIds,
          contactIds: resolvedContactIds,
        });
      }

      onAdd(selectedLists);
      onOpenChange(false);
    } catch (saveError) {
      toast({
        variant: 'destructive',
        title: 'Failed to update lists',
        description:
          saveError instanceof Error
            ? saveError.message
            : 'Could not update contact list membership.',
      });
    }
  };

  const showLoading = (isLoading || isFetching) && lists.length === 0;
  const isSaving = isSyncing || isAdding || isCreating || isAddingCreatedMembers || isStartingJob;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-0 overflow-hidden p-0 sm:rounded-xl">
        <DialogHeader className="space-y-1 border-b px-6 py-5">
          <DialogTitle className="text-2xl font-semibold tracking-tight">
            Add to List
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose one or more lists for{' '}
            <span className="font-medium text-foreground">{contactName}</span>.
          </p>
        </DialogHeader>

        <div className="space-y-4 px-6 py-4">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search lists"
              className="h-10 pl-9 focus-visible:border-[#0161AB] focus-visible:ring-[#0161AB]"
              aria-label="Search lists"
              disabled={isSaving}
            />
          </div>

          {isCreatingList ? (
            <div className="space-y-3 rounded-lg border border-dashed p-4">
              <p className="text-sm font-medium text-foreground">Create new list</p>
              <Input
                value={newListName}
                onChange={(event) => setNewListName(event.target.value)}
                placeholder="List name"
                className="h-10 focus-visible:border-[#0161AB] focus-visible:ring-[#0161AB]"
                aria-label="New list name"
                disabled={isCreating}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleCreateList();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsCreatingList(false);
                    setNewListName('');
                  }}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-[#0161AB] text-white hover:bg-[#01558F]"
                  onClick={() => void handleCreateList()}
                  disabled={!newListName.trim() || isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create list'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="h-10 w-full justify-start gap-2 border-dashed text-[#0161AB] hover:bg-[#0161AB]/5 hover:text-[#0161AB]"
              onClick={() => setIsCreatingList(true)}
              disabled={isSaving}
            >
              <FolderPlusIcon className="h-4 w-4" />
              Create new list
            </Button>
          )}

          <div className="max-h-[320px] space-y-1 overflow-y-auto rounded-lg border p-1">
            {showLoading && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Loading lists...
              </p>
            )}

            {!showLoading && isError && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'Failed to load lists'}
              </p>
            )}

            {!showLoading && !isError && filteredLists.length === 0 && (
              <div className="px-3 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {search.trim() ? 'No lists match your search.' : 'No lists found.'}
                </p>
                {!search.trim() && !isCreatingList ? (
                  <Button
                    type="button"
                    variant="link"
                    className="mt-2 h-auto p-0 text-[#0161AB]"
                    onClick={() => setIsCreatingList(true)}
                    disabled={isSaving}
                  >
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Create your first list
                  </Button>
                ) : null}
              </div>
            )}

            {!showLoading &&
              !isError &&
              filteredLists.map((list) => {
                const listName = list.name.trim();
                const isChecked = selectedLists.includes(listName);
                const alreadyMember = !isBulk && memberListNames.includes(listName);

                return (
                  <label
                    key={list.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-muted/50 ${
                      isChecked ? 'bg-[#0161AB]/5' : ''
                    }`}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleList(listName)}
                      disabled={isSaving}
                      className="border-[#0161AB] data-[state=checked]:border-[#0161AB] data-[state=checked]:bg-[#0161AB]"
                    />
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#0161AB]/10">
                      <FolderIcon className="h-4 w-4 text-[#0161AB]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{listName}</p>
                      {alreadyMember ? (
                        <p className="text-xs text-muted-foreground">Already a member</p>
                      ) : null}
                    </div>
                  </label>
                );
              })}
          </div>
        </div>

        <DialogFooter className="gap-2 border-t px-6 py-4 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-[#0161AB] text-white hover:bg-[#01558F]"
            onClick={() => void handleAdd()}
            disabled={!hasSelectionChanged || showLoading || isError || isSaving}
          >
            {isSaving
              ? 'Saving...'
              : newlySelectedCount > 0
                ? `Add to ${newlySelectedCount} list${newlySelectedCount === 1 ? '' : 's'}`
                : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
